/**
 * Notebook domain event schemas.
 *
 * Typed payload validation for Cortex Notebooks Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module notebook.events
 * @since 2026-05-06
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Shared payload fragments ────────────────────────────────────────────

const NotebookBasePayload = z.object({
  notebookId: z.string(),
  organizationId: z.string(),
  userId: z.string().optional(),
  source: z.string(),
})

const SourceBasePayload = z.object({
  notebookId: z.string(),
  sourceId: z.string(),
  organizationId: z.string().optional(),
  userId: z.string().optional(),
  sourceType: z.enum(['document', 'note', 'url', 'paste']).optional(),
  source: z.string(),
})

// ── Notebook lifecycle events ───────────────────────────────────────────

export const NotebookCreatedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('notebook_created'),
  payload: NotebookBasePayload.extend({
    title: z.string(),
    geminiStoreId: z.string().nullable().optional(),
  }),
})

export const NotebookUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('notebook_updated'),
  payload: NotebookBasePayload.extend({
    changes: z.record(z.unknown()).optional(),
  }),
})

export const NotebookArchivedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('notebook_archived'),
  payload: NotebookBasePayload,
})

export const NotebookDeletedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('notebook_deleted'),
  payload: NotebookBasePayload.extend({
    geminiStoreId: z.string().nullable().optional(),
  }),
})

// ── Source lifecycle events ─────────────────────────────────────────────

export const NotebookSourceAddedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('source_added'),
  payload: SourceBasePayload.extend({
    title: z.string(),
    filePath: z.string().nullable().optional(),
    mimeType: z.string().optional(),
    fileSize: z.number().optional(),
  }),
})

export const NotebookSourceIndexedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('source_indexed'),
  payload: SourceBasePayload.extend({
    geminiFileId: z.string().nullable().optional(),
    durationMs: z.number().optional(),
    tokenCount: z.number().nullable().optional(),
  }),
})

export const NotebookSourceFailedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('source_failed'),
  payload: SourceBasePayload.extend({
    error: z.string(),
    attempt: z.number().optional(),
    retryable: z.boolean().optional(),
  }),
})

export const NotebookSourceRemovedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('source_removed'),
  payload: SourceBasePayload,
})

export const NotebookSourceDiscoveredEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('source_discovered'),
  payload: z.object({
    notebookId: z.string(),
    organizationId: z.string().optional(),
    topic: z.string(),
    suggestions: z.array(
      z.object({
        title: z.string(),
        url: z.string(),
        snippet: z.string().optional(),
      }),
    ),
    source: z.string(),
  }),
})

// ── Query / interaction events ──────────────────────────────────────────

export const NotebookQueriedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('notebook_queried'),
  payload: NotebookBasePayload.extend({
    query: z.string(),
    model: z.string().optional(),
    contextCacheUsed: z.boolean().optional(),
    citationCount: z.number().optional(),
    durationMs: z.number().optional(),
    tokenUsage: z
      .object({
        promptTokens: z.number().optional(),
        candidatesTokens: z.number().optional(),
        totalTokens: z.number().optional(),
      })
      .optional(),
  }),
})

// ── Audio overview / studio events ──────────────────────────────────────

export const NotebookAudioOverviewRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('audio_overview_requested'),
  payload: NotebookBasePayload.extend({
    focus: z.string().optional(),
    audience: z.string().optional(),
    language: z.string().optional(),
  }),
})

export const NotebookAudioOverviewReadyEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('audio_overview_ready'),
  payload: NotebookBasePayload.extend({
    audioUrl: z.string(),
    durationSeconds: z.number().optional(),
    language: z.string().optional(),
  }),
})

// ── Entity-link events ──────────────────────────────────────────────────

const NotebookLinkPayload = z.object({
  notebookId: z.string(),
  organizationId: z.string(),
  targetType: z.enum([
    'client',
    'project',
    'task',
    'organization',
    'cortex_session',
    'contact',
    'deal',
    'invoice',
    'document',
  ]),
  targetId: z.string(),
  userId: z.string().optional(),
  source: z.string(),
})

export const NotebookLinkedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('notebook_linked'),
  payload: NotebookLinkPayload,
})

export const NotebookUnlinkedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('notebook_unlinked'),
  payload: NotebookLinkPayload,
})

export const NotebookLinkSuggestedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('notebook_link_suggested'),
  payload: NotebookLinkPayload.extend({
    confidence: z.number().min(0).max(1).optional(),
    rationale: z.string().optional(),
  }),
})

// ── Retention lifecycle events ──────────────────────────────────────────

export const RetentionScanRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('retention_scan_requested'),
  payload: z.object({
    scanType: z.enum(['archive', 'purge']).optional(),
    olderThanDays: z.number().optional(),
    batchSize: z.number().optional(),
    source: z.string(),
  }),
})

export const RetentionArchiveDueEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('retention_archive_due'),
  payload: NotebookBasePayload.extend({
    lastExportedAt: z.string().nullable().optional(),
    sourceCount: z.number().optional(),
    hasAudio: z.boolean().optional(),
    reason: z.string().optional(),
  }),
})

export const RetentionExportRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('retention_export_requested'),
  payload: NotebookBasePayload.extend({
    exportType: z.enum(['user_requested', 'scheduled_archive', 'account_deletion']).optional(),
    storageTarget: z.enum(['drive', 'local']).optional(),
    source: z.string(),
  }),
})

export const RetentionArchivedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('retention_archived'),
  payload: NotebookBasePayload.extend({
    exportId: z.string().optional(),
    driveFolderId: z.string().optional(),
    bytesExported: z.number().optional(),
    sourceCount: z.number().optional(),
  }),
})

export const RetentionPurgeDueEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('retention_purge_due'),
  payload: NotebookBasePayload.extend({
    driveFolderId: z.string().nullable().optional(),
    lastExportedAt: z.string().nullable().optional(),
    daysSinceArchive: z.number().optional(),
  }),
})

export const RetentionPurgedEvent = BackboneEventSchema.extend({
  domain: z.literal('notebook'),
  type: z.literal('retention_purged'),
  payload: NotebookBasePayload.extend({
    exportId: z.string().optional(),
    driveFolderId: z.string().nullable().optional(),
    sourceCount: z.number().optional(),
    reason: z.string().optional(),
  }),
})

// ── All notebook events ─────────────────────────────────────────────────

export const NotebookDomainEvents = [
  NotebookCreatedEvent,
  NotebookUpdatedEvent,
  NotebookArchivedEvent,
  NotebookDeletedEvent,
  NotebookSourceAddedEvent,
  NotebookSourceIndexedEvent,
  NotebookSourceFailedEvent,
  NotebookSourceRemovedEvent,
  NotebookSourceDiscoveredEvent,
  NotebookQueriedEvent,
  NotebookAudioOverviewRequestedEvent,
  NotebookAudioOverviewReadyEvent,
  NotebookLinkedEvent,
  NotebookUnlinkedEvent,
  NotebookLinkSuggestedEvent,
  RetentionScanRequestedEvent,
  RetentionArchiveDueEvent,
  RetentionExportRequestedEvent,
  RetentionArchivedEvent,
  RetentionPurgeDueEvent,
  RetentionPurgedEvent,
] as const
